import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { CheckCircle2, ShieldAlert, BookOpenCheck, ArrowUpRight } from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "Submission",
    duration: "Day 0",
    text:
      "Authors submit through our editorial portal. Automated conformity checks verify structure, ethics statements, and conflict-of-interest declarations.",
  },
  {
    n: "02",
    title: "Editorial Assessment",
    duration: "24–48 hours",
    text:
      "Editor-in-Chief reviews scope, originality, and methodological soundness. Desk decisions issued within 48 hours.",
  },
  {
    n: "03",
    title: "Peer Review",
    duration: "7–14 days",
    text:
      "Double-blind external review by at least two independent subject experts. Structured reviewer guidelines ensure clinical and statistical rigor.",
  },
  {
    n: "04",
    title: "Revision",
    duration: "Author-paced",
    text:
      "Authors respond to reviewer comments with traceable revisions. Second-round review when required to verify quality of changes.",
  },
  {
    n: "05",
    title: "Acceptance",
    duration: "Median: Day 14",
    text:
      "Final editorial decision based on reviewer recommendations, reproducibility, and contribution to medical knowledge.",
  },
  {
    n: "06",
    title: "Publication",
    duration: "Within 48h",
    text:
      "Articles typeset, registered with Crossref DOI, archived in CLOCKSS / Portico, and released under open-access CC-BY 4.0 licensing.",
  },
];

const ETHICS = [
  { label: "COPE Principles", text: "Best Practice Guidelines for Journal Editors" },
  { label: "Research Ethics", text: "Declaration of Helsinki & ICMJE adherence" },
  { label: "Conflict of Interest", text: "Mandatory disclosure for all contributors" },
  { label: "Plagiarism Screening", text: "iThenticate verification on all submissions" },
  { label: "Author Rights", text: "Open access · CC-BY 4.0 licensing" },
];

const EQUATOR_GUIDELINES = [
  {
    acronym: "CONSORT",
    title: "Randomized Controlled Trials",
    href: "https://www.equator-network.org/reporting-guidelines/consort/",
    color: "var(--sci-teal)",
  },
  {
    acronym: "STROBE",
    title: "Observational Studies",
    href: "https://www.equator-network.org/reporting-guidelines/strobe/",
    color: "var(--sci-navy)",
  },
  {
    acronym: "PRISMA",
    title: "Systematic Reviews",
    href: "https://www.equator-network.org/reporting-guidelines/prisma/",
    color: "var(--sci-teal)",
  },
  {
    acronym: "CARE",
    title: "Case Reports",
    href: "https://www.equator-network.org/reporting-guidelines/care/",
    color: "var(--sci-navy)",
  },
  {
    acronym: "SQUIRE",
    title: "Quality Improvement",
    href: "https://www.equator-network.org/reporting-guidelines/squire/",
    color: "var(--sci-teal)",
  },
  {
    acronym: "ARRIVE",
    title: "Animal Research",
    href: "https://www.equator-network.org/reporting-guidelines/arrive/",
    color: "var(--sci-navy)",
  },
  {
    acronym: "SPIRIT",
    title: "Clinical Trial Protocols",
    href: "https://www.equator-network.org/reporting-guidelines/spirit-2013-statement-defining-standard-protocol-items-for-clinical-trials/",
    color: "var(--sci-teal)",
  },
  {
    acronym: "STARD",
    title: "Diagnostic Accuracy",
    href: "https://www.equator-network.org/reporting-guidelines/stard/",
    color: "var(--sci-navy)",
  },
  {
    acronym: "COREQ",
    title: "Qualitative Research",
    href: "https://www.equator-network.org/reporting-guidelines/coreq/",
    color: "var(--sci-teal)",
  },
];

export default function Standards() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 70%", "end 30%"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="standards"
      data-testid="standards-section"
      className="relative py-24 md:py-32 bg-white border-y border-[var(--sci-border)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-16 md:mb-24">
          <div className="overline text-[var(--sci-teal)] mb-5">
            · 04 / Our Publishing Standards
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]">
            A transparent path <br />
            <em className="not-italic text-[var(--sci-teal)]">from manuscript to medicine</em>.
          </h2>
        </div>

        {/* Timeline */}
        <div ref={ref} className="relative pl-8 md:pl-0">
          {/* Track */}
          <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-px bg-[var(--sci-border)]" />
          <motion.div
            style={{ height: lineHeight }}
            className="absolute left-3 md:left-1/2 top-0 w-px bg-[var(--sci-teal)]"
          />

          <div className="space-y-14 md:space-y-24">
            {STEPS.map((s, i) => {
              const onLeft = i % 2 === 0;
              return (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.6, delay: 0.05 * i }}
                  data-testid={`standards-step-${i}`}
                  className="relative md:grid md:grid-cols-2 md:gap-16 items-start"
                >
                  {/* Node */}
                  <div className="absolute left-3 md:left-1/2 -translate-x-1/2 top-1.5 w-3.5 h-3.5 bg-white border-2 border-[var(--sci-teal)] sci-pulse-dot" />

                  <div
                    className={`pl-10 md:pl-0 ${
                      onLeft ? "md:pr-12 md:text-right" : "md:col-start-2 md:pl-12"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-3 mb-3 ${
                        onLeft ? "md:justify-end" : ""
                      }`}
                    >
                      <span className="overline text-[var(--sci-text-muted)]">
                        Step {s.n}
                      </span>
                      <span className="font-mono-sci text-[10px] tracking-[0.18em] uppercase text-[var(--sci-teal)] border border-[var(--sci-teal)]/40 px-2 py-1">
                        {s.duration}
                      </span>
                    </div>
                    <h3 className="font-serif-display text-2xl md:text-4xl text-[var(--sci-navy)] tracking-tight leading-tight">
                      {s.title}
                    </h3>
                    <p
                      className={`mt-4 text-sm md:text-[15px] leading-relaxed text-[var(--sci-text-muted)] max-w-md ${
                        onLeft ? "md:ml-auto" : ""
                      }`}
                    >
                      {s.text}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Ethics callout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          data-testid="standards-ethics-callout"
          className="mt-24 md:mt-32 bg-[var(--sci-navy)] text-white p-8 md:p-14 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
          <div className="relative grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <ShieldAlert size={18} className="text-[var(--sci-teal)]" />
                <span className="overline text-white/70">Ethics & Standards</span>
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">
                Anchored in international research ethics.
              </h3>
              <p className="mt-5 text-white/70 leading-relaxed">
                Every article published under the Sci Ink Press imprint complies
                with the following internationally recognized standards.
              </p>
            </div>
            <ul className="md:col-span-7 grid sm:grid-cols-2 gap-5">
              {ETHICS.map((e, i) => (
                <li
                  key={e.label}
                  data-testid={`ethics-item-${i}`}
                  className="flex items-start gap-3 border border-white/10 p-5 hover:border-[var(--sci-teal)] transition-colors"
                >
                  <CheckCircle2
                    size={18}
                    className="text-[var(--sci-teal)] shrink-0 mt-0.5"
                    strokeWidth={1.6}
                  />
                  <div>
                    <div className="font-serif-display text-lg">{e.label}</div>
                    <div className="text-xs text-white/65 mt-1.5 font-mono-sci tracking-wider">
                      {e.text}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* EQUATOR Network widget */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.7 }}
          data-testid="equator-widget"
          className="mt-12 md:mt-16 relative bg-white border border-[var(--sci-border)] overflow-hidden"
        >
          <div className="grid lg:grid-cols-12 gap-0">
            {/* Left: copy + EQUATOR brand */}
            <div className="lg:col-span-4 p-8 md:p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-[var(--sci-border)] bg-[var(--sci-bg)] relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 border border-[var(--sci-teal)] flex items-center justify-center text-[var(--sci-teal)]">
                  <BookOpenCheck size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="overline text-[var(--sci-teal)]">
                    EQUATOR Network
                  </div>
                  <div className="font-mono-sci text-[10px] tracking-wider text-[var(--sci-text-muted)] mt-1">
                    equator-network.org
                  </div>
                </div>
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl lg:text-[34px] text-[var(--sci-navy)] tracking-tight leading-[1.05]">
                Reporting Guidelines.
              </h3>
              <p className="mt-5 text-sm md:text-[15px] text-[var(--sci-text-muted)] leading-relaxed">
                All submissions to Sci Ink Press journals must adhere to the
                appropriate reporting standard maintained by the{" "}
                <span className="text-[var(--sci-navy)] font-medium">
                  EQUATOR Network
                </span>{" "}
                — the global authority on transparent health-research reporting.
              </p>
              <a
                href="https://www.equator-network.org/reporting-guidelines/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="equator-library-cta"
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[var(--sci-navy)] hover:text-[var(--sci-teal)] border-b border-[var(--sci-navy)] hover:border-[var(--sci-teal)] pb-0.5 transition-colors"
              >
                Browse full guideline library
                <ArrowUpRight size={14} />
              </a>

              {/* Footnote stat */}
              <div className="mt-10 border-t border-[var(--sci-border)] pt-6">
                <div className="font-serif-display text-4xl text-[var(--sci-navy)] leading-none">
                  500+
                </div>
                <div className="overline text-[var(--sci-text-muted)] mt-2">
                  Reporting Guidelines Indexed
                </div>
              </div>
            </div>

            {/* Right: guideline grid */}
            <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[var(--sci-border)]">
              {EQUATOR_GUIDELINES.map((g, i) => (
                <a
                  key={g.acronym}
                  href={g.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`equator-guideline-${i}`}
                  className="group bg-white p-5 md:p-6 hover:bg-[var(--sci-bg)] transition-colors relative"
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform"
                    style={{ background: g.color }}
                  />
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="font-mono-sci text-[10px] tracking-[0.22em]"
                      style={{ color: g.color }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <ArrowUpRight
                      size={14}
                      className="text-[var(--sci-text-muted)] group-hover:text-[var(--sci-teal)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                    />
                  </div>
                  <div
                    className="font-serif-display text-xl md:text-2xl leading-none tracking-tight"
                    style={{ color: g.color }}
                  >
                    {g.acronym}
                  </div>
                  <div className="text-xs md:text-[13px] text-[var(--sci-text-muted)] mt-2.5 leading-snug">
                    {g.title}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
